/**
 * Self-service node creation (backend routers/node_signups.py,
 * docs/self-service-node-creation-todo.md).
 *
 * useSlugAvailability: debounced GET /api/node-signups/availability?slug= —
 * whether a label can become a node here, the address it would get, and a
 * free neighbour when it is taken.
 *
 * useNodeSignup: POST /api/node-signups creates the node and provisions it
 * before answering, so `signup.status` is final when it comes back: "ready",
 * "identity_pending" (the node is live; sign-in on its address is not set up,
 * `identity_error` says why — usually an operator step; retry finishes it) or
 * "failed" (retry resumes at the failed step). `can_retry` says whether a
 * retry would run now. Coming back to the page resumes the newest unfinished
 * signup (GET /api/node-signups).
 */

import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import debounce from 'lodash/debounce';
import { useApi } from './use_api';
import { isValidSlug } from '../utils/node_slug';

const AVAILABILITY_DEBOUNCE_MS = 300;

export function useSlugAvailability() {
    const api = useApi();
    const [result, setResult] = useState(null);
    const [checking, setChecking] = useState(false);
    const latest = useRef('');

    const check = useMemo(() => debounce(slug => {
        latest.current = slug;
        if (!slug) { setResult(null); setChecking(false); return; }
        if (!isValidSlug(slug)) {
            setResult({ slug, available: false, reason: 'invalid', suggestion: null, hostname: null });
            setChecking(false);
            return;
        }
        api.get('/api/node-signups/availability', { params: { slug } })
            .then(data => { if (latest.current === slug) setResult(data); })
            .catch(() => { if (latest.current === slug) setResult(null); })
            .finally(() => { if (latest.current === slug) setChecking(false); });
    }, AVAILABILITY_DEBOUNCE_MS), [api]);

    useEffect(() => () => check.cancel(), [check]);

    const request = useCallback(slug => {
        setChecking(!!slug);
        check(slug);
    }, [check]);

    return { check: request, result, checking };
}

// Signups the person may still need to act on: shown again when they come
// back to /nodes/new, so a Retry after an operator fixed sign-in (or after
// the request that ran the job was cut off) is one click, not a support case.
const UNFINISHED = ['identity_pending', 'failed', 'provisioning', 'pending'];

export function useNodeSignup() {
    const api = useApi();
    const [signup, setSignup] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const touched = useRef(false);

    // Resume the newest unfinished signup, unless this visit already made one.
    useEffect(() => {
        let cancelled = false;
        Promise.resolve(api.get('/api/node-signups'))
            .then(list => {
                if (cancelled || touched.current || !Array.isArray(list)) return;
                const open = list.find(s => UNFINISHED.includes(s.status));
                if (open) setSignup(open);
            })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [api]);

    const create = useCallback(({ name, slug }) => {
        touched.current = true;
        setBusy(true);
        setError(null);
        return api.post('/api/node-signups', { name, slug: slug || null, plan: 'trial' }, { invalidate: '/api/nodes/mine' })
            .then(data => { setSignup(data); return data; })
            .catch(err => { setError(err); throw err; })
            .finally(() => setBusy(false));
    }, [api]);

    const retry = useCallback(() => {
        if (!signup) return Promise.resolve(null);
        touched.current = true;
        setBusy(true);
        setError(null);
        return api.post(`/api/node-signups/${signup.id}/retry`, undefined, { invalidate: '/api/nodes/mine' })
            .then(data => { setSignup(data); return data; })
            .catch(err => { setError(err); throw err; })
            .finally(() => setBusy(false));
    }, [api, signup]);

    // Re-read the signup (a "provisioning" one another request is running).
    const refresh = useCallback(() => {
        if (!signup) return Promise.resolve(null);
        setBusy(true);
        setError(null);
        return api.get(`/api/node-signups/${signup.id}`)
            .then(data => { setSignup(data); return data; })
            .catch(err => { setError(err); throw err; })
            .finally(() => setBusy(false));
    }, [api, signup]);

    const reset = useCallback(() => { touched.current = true; setSignup(null); setError(null); }, []);

    return { signup, create, retry, refresh, busy, error, reset };
}
